import { Injectable, Type } from '@angular/core';
import {
  MatSnackBar,
  MatSnackBarConfig,
  MatSnackBarHorizontalPosition,
  MatSnackBarRef,
} from '@angular/material/snack-bar';
import { merge } from 'lodash-es';
import { take } from 'rxjs';
import { ToastComponent } from './normal-toast/toast.component';
import { TOAST_COMPONENT_REF, TOAST_DEFAULT_DURATION, TOAST_PANEL_CLASS, TOAST_POSITION } from './toast.constant';
import { ToastBaseComponent, ToastData, ToastType } from './toast.type';

interface QueuedToast {
  component: Type<ToastBaseComponent>;
  config: MatSnackBarConfig<ToastData>;
}

@Injectable()
export class ToastService {
  constructor(private _snackBar: MatSnackBar) {}

  private _defaultOptions: MatSnackBarConfig<ToastData> = {
    duration: TOAST_DEFAULT_DURATION,
    horizontalPosition: 'right',
  };

  /** The toast currently on screen. `null` once it has finished leaving. */
  private _openedRef: MatSnackBarRef<ToastBaseComponent> | null = null;

  /** Toast waiting for the current one to leave. Only the newest one is kept. */
  private _queued: QueuedToast | null = null;

  // Hutton version
  open(data: ToastData): void {
    this._enqueue(ToastComponent, {
      duration: data.duration || 3000,
      panelClass: 'toast-container',
      data,
    });
  }

  // CIMB version
  notify(data: ToastData): void {
    // Skip empty toasts (e.g. errors already handled globally by HttpErrorInterceptor).
    if (!data.messages && !data.title) return;

    this._enqueue(this._getComponentRef(data.type), this._normalizeConfig(data));
  }

  /**
   * Show a toast, but never while another one is still on screen.
   *
   * SME-3577: `MatSnackBar` lets you open on top of a live toast, and handles it by attaching the new
   * container to the DOM immediately while deferring `containerInstance.enter()` — the *only*
   * `detectChanges()` in the snack bar — until the old one's exit animation completes. Until then the
   * new container sits in the page having never been change-detected: interpolations are empty,
   * `MatIcon.ngOnInit()` has not added the `material-icons` class (so `<mat-icon>done</mat-icon>`
   * paints the raw ligature text, clipped to the 24px icon box), and the `void` animation state that
   * would have kept it invisible is not applied either. That is the blank green bar in the ticket.
   *
   * So we hold the toast here instead and open nothing until the previous one is really gone. Worst
   * case a toast shows up late; it can no longer show up broken.
   */
  private _enqueue(component: Type<ToastBaseComponent>, config: MatSnackBarConfig<ToastData>): void {
    if (this._openedRef) {
      // Newest message wins — same as the old behaviour, where opening replaced what was on screen.
      this._queued = { component, config };
      this._openedRef.dismiss();
      return;
    }

    this._show({ component, config });
  }

  private _show({ component, config }: QueuedToast): void {
    const ref = this._snackBar.openFromComponent(component, config);
    this._openedRef = ref;

    ref
      .afterDismissed()
      .pipe(take(1))
      .subscribe(() => {
        if (this._openedRef === ref) this._openedRef = null;

        const queued = this._queued;
        this._queued = null;
        if (queued) this._show(queued);
      });
  }

  private _normalizeConfig(data: ToastData): MatSnackBarConfig<ToastData> {
    const { duration, type } = data;
    const customConfig: MatSnackBarConfig<ToastData> = {
      duration,
      data,
    };

    const horizontalPosition: MatSnackBarHorizontalPosition = TOAST_POSITION[type];
    const panelClass: string = TOAST_PANEL_CLASS[type];

    return merge({}, this._defaultOptions, { horizontalPosition, panelClass }, customConfig);
  }

  private _getComponentRef(type: ToastType): Type<ToastBaseComponent> {
    return TOAST_COMPONENT_REF[type];
  }
}
